import {
  Box,
  MaterialSymbol,
  Card,
  CardTabs,
  usePagination,
  useUpdateQueryParams,
  Pagination,
  withStyles,
} from 'vopay';
import type { SubscriptionItem } from 'vopay';
import { FunctionComponent, useState, useMemo, useEffect } from 'react';
import { TableFiltersContainer, Title, Button } from 'UI';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import ReportCreationDrawer from './Dialogue/ReportCreation/ReportCreationDrawer';
import ReportsTable from './ReportsTable';
import OneTimeReportSearchForm from './OneTimeReportSearchForm';
import SubscriptionListSearchForm from './SubscriptionListSearchForm';

import { useClientData } from '@/providers/ClientData.provider';
import useFetchSubscriptions from '@/hooks/useApi/useFetchSubscriptions';

import { useAlertDialog } from '@/providers/AlertDialog.provider';

import styles from './ReportsPage.module.scss';

const classes = withStyles(styles);

type FC = FunctionComponent;

type QueryParams = {
  paymentMethods: string;
  scheduleType: string;
  reportType: string;
  frequency: string;
  searchTerm: string;
};

type ReportsSearchParams = {
  paymentMethods?: string;
  scheduleType?: string;
  reportType?: string;
  frequency?: string;
  searchTerm?: string;
};

type TabType = 'one-time' | 'subscription';

const ReportsContainer: FC = () => {
  const location = useLocation();
  const { response } = useClientData();
  const [subscriptionsResponse, , , fetchSubscriptions] = useFetchSubscriptions([]);
  const { t } = useTranslation('translations', { keyPrefix: 'Reports' });
  const alertDialog = useAlertDialog();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      paymentMethods: params.get('paymentMethods') || '',
      scheduleType: params.get('scheduleType') || '',
      reportType: params.get('reportType') || '',
      frequency: params.get('frequency') || '',
      searchTerm: params.get('searchTerm') || '',
    };
  }, [location.search]);

  const paymentMethods = useMemo(() => queryParams.paymentMethods || '', [queryParams]);
  const scheduleType = useMemo(() => queryParams.scheduleType || '', [queryParams]);
  const reportType = useMemo(() => queryParams.reportType || '', [queryParams]);
  const frequency = useMemo(() => queryParams.frequency || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.searchTerm || '', [queryParams]);
  const filtersApplied = useMemo(() => {
    return Object.entries(queryParams)
      .filter(([key]) => key !== 'scheduleType')
      .some(([, val]) => !!val);
  }, [queryParams]);
  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const [activeTab, setActiveTab] = useState<TabType>((scheduleType as TabType) || 'one-time');
  const [filteredList, setFilteredList] = useState<SubscriptionItem[]>([]);
  const [oneTimeTotalData, setOneTimeTotalData] = useState<number | undefined>(undefined);
  const [subscriptionTotalData, setSubscriptionTotalData] = useState<number | undefined>(undefined);

  const handleTabChange = (value: string) => {
    if (value === 'one-time' || value === 'subscription') {
      setActiveTab(value);
    }
  };

  // pagination
  const totalDataLength = useMemo(() => {
    return subscriptionsResponse.PaginationMetaData?.TotalRecords || 0;
  }, [filteredList]);
  const PER_PAGE = 10;
  const { pageCount, pageIndex, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const fetchReports = async (offset = 0, searchParams: ReportsSearchParams = {}) => {
    const defaultValues = {
      PortalUser: response.LoginUserName,
      PaymentRails: searchParams.paymentMethods ?? '',
      SearchText: searchParams.searchTerm ?? searchTerm,
      ScheduleType: activeTab === 'subscription' ? (searchParams.scheduleType ?? scheduleType) : 'one-time',
      AccountTransactionReportType: searchParams.reportType ?? reportType,
      Frequency: activeTab === 'subscription' ? (searchParams.frequency ?? frequency) : '',
      Limit: PER_PAGE,
      Offset: offset,
    };
    try {
      const res = await fetchSubscriptions(defaultValues);

      if (!res.Success) throw new Error(res.ErrorMessage);
      const subscriptionRequest = res.Subscriptions;
      setFilteredList(subscriptionRequest);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchSubscriptions');
      alertDialog.error(t('ErrorMessages.error'), msg);
      console.error(err);
    }
  };

  const runSearch = () => {
    const offset = setPageIndex(0);
    let scheduleType = activeTab === 'subscription' ? 'subscription' : 'one-time';
    const searchParams = {
      paymentMethods,
      scheduleType,
      reportType,
      searchTerm,
      ...(scheduleType === 'subscription' ? { frequency } : { frequency: undefined }),
    };

    updateQueryParams(searchParams);
    fetchReports(offset, searchParams);
  };

  useEffect(() => {
    runSearch();
  }, [activeTab]);

  useEffect(() => {
    const fetchData = async (scheduleType: 'one-time' | 'subscription') => {
      const defaultValues = {
        PortalUser: response.LoginUserName,
        ScheduleType: scheduleType,
        Offset: 1,
      };
      try {
        const res = await fetchSubscriptions(defaultValues);

        if (!res.Success) throw new Error(res.ErrorMessage);
        if (scheduleType === 'one-time') {
          setOneTimeTotalData(res.PaginationMetaData?.TotalRecords);
        } else {
          setSubscriptionTotalData(res.PaginationMetaData?.TotalRecords);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchSubscriptions');
        alertDialog.error(t('ErrorMessages.error'), msg);
        console.error(err);
      }
    };
    fetchData('one-time');
    fetchData('subscription');
  }, [filteredList]);

  const noReports = (type: string, isAllEmpty = false) => (
    <Box flx="col" jc="center" ai="center">
      <MaterialSymbol icon="description" size="108px" fillAmount={1} color="rgb(45, 45, 45, 0.75)" />
      <Title level="3">
        {t('FirstReport.title', {
          label: type,
        })}
      </Title>
      <span className={classes('noReportDescription')}>{t('FirstReport.description')}</span>
      <Button onClick={() => setIsDrawerOpen(true)} colorScheme="primary">
        {t('FirstReport.buttonTitle', {
          label: type,
        })}
      </Button>
      <ReportCreationDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={runSearch}
        disabledFields={isAllEmpty ? [] : ['ScheduleType']}
        initialFields={activeTab === 'subscription' ? { ScheduleType: 'subscription' } : { ScheduleType: 'one-time' }}
        title={t('FirstReport.buttonTitle', {
          label: type,
        })}
        subscriptions={filteredList}
      />
    </Box>
  );

  return (
    <div>
      <Box flx="col" jc="sb" gapSize="36px">
        {oneTimeTotalData === 0 && subscriptionTotalData === 0 && subscriptionsResponse.Success ? (
          <>
            <Title>{t('pageTitle')}</Title>
            <Card
              style={{
                padding: '24px 16px',
                height: '60vh',
              }}
            >
              {noReports(t('NoReport.report'), true)}
            </Card>
          </>
        ) : (
          <>
            <Box ai="center" jc="sb">
              <Title>{t('pageTitle')}</Title>
              {(activeTab === 'one-time' && oneTimeTotalData !== 0) ||
              (activeTab === 'subscription' && subscriptionTotalData !== 0) ? (
                <>
                  <div style={{ marginLeft: 'auto' }}>
                    <Button onClick={() => setIsDrawerOpen(true)} colorScheme="primary">
                      {t('ReportTable.createReportButton')}
                    </Button>
                  </div>
                  <ReportCreationDrawer
                    isOpen={isDrawerOpen}
                    onClose={() => setIsDrawerOpen(false)}
                    onSuccess={runSearch}
                    title={t('ReportTable.createReportButton')}
                    subscriptions={filteredList}
                  />
                </>
              ) : null}
            </Box>
            <Box flx="col" position="relative">
              <CardTabs.Root value={activeTab} onValueChange={handleTabChange}>
                <CardTabs.List aria-label="One-time Report">
                  <CardTabs.Trigger value="one-time">
                    <span>{t('ReportTable.TabTitle.oneTime')}</span>
                  </CardTabs.Trigger>
                  <CardTabs.Trigger value="subscription">
                    <span>{t('ReportTable.TabTitle.subscription')}</span>
                  </CardTabs.Trigger>
                </CardTabs.List>

                <CardTabs.Content value="one-time">
                  <Box flx="col">
                    {oneTimeTotalData === 0 ? (
                      <div
                        style={{
                          padding: '24px 16px',
                          height: '60vh',
                        }}
                      >
                        {noReports(t('NoReport.oneTime'))}
                      </div>
                    ) : (
                      <>
                        <Box flx="col" gap="sm">
                          <TableFiltersContainer filtersApplied={filtersApplied}>
                            <TableFiltersContainer.Trigger />

                            <TableFiltersContainer.Content>
                              <OneTimeReportSearchForm
                                paymentMethods={paymentMethods}
                                reportType={reportType}
                                searchTerm={searchTerm}
                                onSearch={(searchParams) => {
                                  const offset = setPageIndex(0);
                                  const params = {
                                    ...searchParams,
                                    paymentMethods: searchParams.paymentMethods.join(','),
                                  };
                                  updateQueryParams(params);
                                  fetchReports(offset, params);
                                }}
                              />
                            </TableFiltersContainer.Content>
                          </TableFiltersContainer>
                        </Box>

                        <ReportsTable reports={filteredList} activeTab={activeTab} />
                        <Box width="100%" jc="center" mt="md" mb="sm">
                          {pageCount > 1 && (
                            <Pagination
                              pageCount={pageCount}
                              forcePageNumber={pageIndex}
                              handlePageClick={(e) => {
                                const offset = setPageIndex(e.selected);
                                const searchParams = {
                                  paymentMethods,
                                  reportType,
                                  scheduleType,
                                  searchTerm,
                                };
                                updateQueryParams(searchParams);
                                fetchReports(offset, searchParams);
                              }}
                            />
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </CardTabs.Content>

                <CardTabs.Content value="subscription">
                  <Box flx="col">
                    {subscriptionTotalData === 0 ? (
                      <div
                        style={{
                          padding: '24px 16px',
                          height: '60vh',
                        }}
                      >
                        {noReports(t('NoReport.subscription'))}
                      </div>
                    ) : (
                      <>
                        <Box flx="col" gap="sm">
                          <TableFiltersContainer filtersApplied={filtersApplied}>
                            <TableFiltersContainer.Trigger />

                            <TableFiltersContainer.Content>
                              <SubscriptionListSearchForm
                                paymentMethods={paymentMethods}
                                reportType={reportType}
                                frequency={frequency}
                                searchTerm={searchTerm}
                                onSearch={(searchParams) => {
                                  const offset = setPageIndex(0);
                                  const params = {
                                    ...searchParams,
                                    paymentMethods: searchParams.paymentMethods.join(','),
                                  };
                                  updateQueryParams(params);
                                  fetchReports(offset, params);
                                }}
                              />
                            </TableFiltersContainer.Content>
                          </TableFiltersContainer>
                        </Box>
                        <ReportsTable reports={filteredList} activeTab={activeTab} onUpdate={runSearch} />
                        <Box width="100%" jc="center" mt="md" mb="sm">
                          {pageCount > 1 && (
                            <Pagination
                              pageCount={pageCount}
                              forcePageNumber={pageIndex}
                              handlePageClick={(e) => {
                                const offset = setPageIndex(e.selected);
                                const searchParams = {
                                  paymentMethods,
                                  scheduleType,
                                  reportType,
                                  frequency,
                                  searchTerm,
                                };
                                updateQueryParams(searchParams);
                                fetchReports(offset, searchParams);
                              }}
                            />
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </CardTabs.Content>
              </CardTabs.Root>
            </Box>
          </>
        )}
      </Box>
    </div>
  );
};

export default ReportsContainer;
